// src/components/MatchesList.tsx
// FIXED VERSION - Resolves match labels from vault and shows verification status
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { generateClient, GraphQLResult } from 'aws-amplify/api';
import { getCurrentUser } from 'aws-amplify/auth';

/* ============================================================================
 * PHONE/EMAIL/HANDLE NORMALIZATION
 * ============================================================================ */
const normalizePhone = (s: string) => {
  const d = (s || "").replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) return `+${d}`;
  if (d.length === 10) return `+1${d}`;
  return d ? `+${d}` : "";
};

const normalizeEmail = (s: string) => (s || "").trim().toLowerCase();

const extractHandle = (s: string) => {
  let v = (s || "").trim();
  v = v.replace(/^https?:\/\/(www\.)?/i, "");
  v = v.replace(/^([^/]+)\//, "");
  v = v.replace(/^@/, "");
  return v.trim().toLowerCase();
};

const normalizeForHash = (raw: string) => {
  const s = (raw || "").trim();
  if (!s) return "";
  
  const digits = s.replace(/\D/g, "");
  if (digits.length >= 10 && digits.length <= 15) return normalizePhone(s);
  
  if (s.includes("@") && /\.[a-z]{2,}$/i.test(s)) return normalizeEmail(s);
  
  return extractHandle(s);
};

/* ============================================================================
 * TYPES
 * ============================================================================ */
type Status = 'ACTIVE' | 'HIDDEN' | 'BLOCKED';
type Level = '1' | '2' | '3';

type MatchParticipant = {
  userId: string;
  pairId: string;
  otherHash: string;
  matchLabel?: string | null;
  status: Status;
  firstSeenAt?: number | null;
  matchedAt?: number | null;
  hiddenAt?: number | null;
  sharedLevel?: Level | null;
  handleVerified?: boolean | null;  // ✅ NEW: Verification status
  owner: string;
};

type ContactPoint = {
  pk: string;
  sk: string;
  owner: string;
  kind: string;
  platform?: string;
  valueNormalized: string;
};

type CrushItem = {
  contact: string;
  interestLevel: string;
  contactHash?: string;
};

type Page<T> = { items: T[]; nextToken?: string | null };

/* ============================================================================
 * GRAPHQL OPERATIONS
 * ============================================================================ */
const Q_LIST_PARTICIPANTS = /* GraphQL */ `
  query ListMatchParticipants(
    $filter: ModelMatchParticipantFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listMatchParticipants(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        userId
        pairId
        otherHash
        matchLabel
        status
        firstSeenAt
        matchedAt
        hiddenAt
        sharedLevel
        handleVerified
        owner
      }
      nextToken
    }
  }
`;

const Q_LIST_CONTACT_POINTS = /* GraphQL */ `
  query ListContactPoints(
    $filter: ModelContactPointFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listContactPoints(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        pk
        sk
        owner
        kind
        platform
        valueNormalized
      }
      nextToken
    }
  }
`;

const M_UPDATE_PARTICIPANT = /* GraphQL */ `
  mutation UpdateMatchParticipant($input: UpdateMatchParticipantInput!) {
    updateMatchParticipant(input: $input) {
      userId
      pairId
      status
      hiddenAt
      owner
    }
  }
`;

/* ============================================================================
 * HELPERS
 * ============================================================================ */
const client = generateClient();
const DEBUG = import.meta.env.VITE_MATCHES_DEBUG === 'true';

function heartsFor(level?: string | null) {
  if (level === '3') return { text: 'down bad', hearts: '❤️❤️❤️' };
  if (level === '2') return { text: 'like like', hearts: '❤️❤️' };
  if (level === '1') return { text: 'curious', hearts: '❤️' };
  return null;
}

function shortHash(h: string) {
  return h.length > 8 ? h.slice(0, 8) : h;
}

/** SHA-256 → hex (Web Crypto) */
async function sha256Hex(input: string): Promise<string> {
  const enc = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest('SHA-256', enc);
  const bytes = Array.from(new Uint8Array(buf));
  return bytes.map(b => b.toString(16).padStart(2, '0')).join('');
}

/** ✅ NEW: Format contact point for display based on kind */
function formatContactLabel(cp: ContactPoint): string {
  const { kind, platform, valueNormalized } = cp;
  
  if (kind === 'handle') {
    // Format as @username (platform)
    const platformName = platform ? ` (${platform})` : '';
    return `@${valueNormalized}${platformName}`;
  }
  
  if (kind === 'phone') {
    // Format phone as (555) 123-4567
    const clean = valueNormalized.replace(/\D/g, '');
    if (clean.startsWith('1') && clean.length === 11) {
      const num = clean.slice(1);
      return `(${num.slice(0, 3)}) ${num.slice(3, 6)}-${num.slice(6)}`;
    }
    if (clean.length === 10) {
      return `(${clean.slice(0, 3)}) ${clean.slice(3, 6)}-${clean.slice(6)}`;
    }
  }
  
  // Email - return as-is
  return valueNormalized;
}

/** ✅ NEW: Build hash→label map from ContactPoints with formatted labels */
async function buildContactLabelIndex(points: ContactPoint[]) {
  const entries: Array<[string, string]> = [];
  
  for (const cp of points) {
    const label = formatContactLabel(cp);
    
    // ✅ FIX: Extract hash from pk (format is "cp#{hash}")
    const hash = cp.pk.startsWith('cp#') ? cp.pk.slice(3) : cp.pk;
    
    void ['[buildContactLabelIndex] Extracted:', {
      pk: cp.pk,
      hash: hash,
      label: label
    }];
    
    entries.push([hash, label]);
  }
  
  return new Map<string, string>(entries);
}

/* ============================================================================
 * DATA FETCHERS
 * ============================================================================ */
async function fetchParticipants(
  userId: string,
  limit = 50,
  includeAll = false
): Promise<MatchParticipant[]> {
  const filter = includeAll
    ? { userId: { eq: userId } }
    : { userId: { eq: userId }, status: { eq: 'ACTIVE' } };

  try {
    const res = await client.graphql({
      query: Q_LIST_PARTICIPANTS,
      variables: { filter, limit },
      authMode: 'userPool',
    }) as GraphQLResult<{ listMatchParticipants?: Page<MatchParticipant> }>;

    if (res.errors?.length) {
      void ['[MatchesList] listMatchParticipants errors:', res.errors];
    }

    const items = res.data?.listMatchParticipants?.items ?? [];
    
    // Sort newest first by matchedAt
    items.sort((a, b) => String(b.matchedAt ?? '').localeCompare(String(a.matchedAt ?? '')));

    if (DEBUG) {
      void ['[MatchesList] GraphQL raw response:', JSON.stringify(res, null, 2)];
    }

    return items;
  } catch (error) {
    void ['[MatchesList] fetchParticipants error:', error];
    throw error;
  }
}

async function fetchContactsForOwner(userId: string, limit = 200): Promise<ContactPoint[]> {
  try {
    const res = await client.graphql({
      query: Q_LIST_CONTACT_POINTS,
      variables: { filter: { owner: { eq: userId } }, limit },
      authMode: 'userPool',
    }) as GraphQLResult<{ listContactPoints?: Page<ContactPoint> }>;

    if (res.errors?.length) {
      void ['[MatchesList] listContactPoints errors:', res.errors];
    }
    
    const items = res.data?.listContactPoints?.items ?? [];

    if (DEBUG) {
      void ['[MatchesList] ContactPoints raw response:', JSON.stringify(res, null, 2)];
    }

    return items;
  } catch (error) {
    void ['[MatchesList] fetchContactsForOwner error:', error];
    throw error;
  }
}

/* ============================================================================
 * COMPONENT
 * ============================================================================ */
export default function MatchesList({
  crushList = [],
  userVault = [],  // ✅ NEW: Vault data passed from parent
}: {
  crushList?: CrushItem[];
  userVault?: Array<{ kind: string; platform?: string; value: string }>;
}) {   
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [participants, setParticipants] = useState<MatchParticipant[]>([]);
  const [contacts, setContacts] = useState<ContactPoint[]>([]);
  const [labelMap, setLabelMap] = useState<Map<string, string>>(new Map());
  const [crushHashMap, setCrushHashMap] = useState<Map<string, string>>(new Map());
  const [vaultHashMap, setVaultHashMap] = useState<Map<string, string>>(new Map());
  const [includeAll, setIncludeAll] = useState<boolean>(DEBUG);
  const [hidingMatchId, setHidingMatchId] = useState<string | null>(null);

  /* ========================================================================
   * ✅ NEW: Build vault hash map for instant label lookup
   * ======================================================================== */
  useEffect(() => {
    (async () => {
      if (!userVault.length) return;
      
      const map = new Map<string, string>();
      
      for (const item of userVault) {
        try {
          // Normalize the value
          const normalized = normalizeForHash(item.value);
          if (!normalized) continue;
          
          // Generate hash
          const hash = await sha256Hex(normalized);
          
          // Format label
          let label: string;
          if (item.kind === 'handle') {
            const platformName = item.platform ? ` (${item.platform})` : '';
            label = `@${normalized}${platformName}`;
          } else if (item.kind === 'phone') {
            const clean = normalized.replace(/\D/g, '');
            if (clean.startsWith('1') && clean.length === 11) {
              const num = clean.slice(1);
              label = `(${num.slice(0, 3)}) ${num.slice(3, 6)}-${num.slice(6)}`;
            } else if (clean.length === 10) {
              label = `(${clean.slice(0, 3)}) ${clean.slice(3, 6)}-${clean.slice(6)}`;
            } else {
              label = normalized;
            }
          } else {
            label = normalized;
          }
          
          map.set(hash, label);
          
        } catch (err) {
          void ['[MatchesList] Error processing vault item:', err];
        }
      }
      
      setVaultHashMap(map);
      void ['[MatchesList] Hash samples:', Array.from(map.entries())];  
      void ['[MatchesList] Vault hash map built:', {
        vaultItems: userVault.length,
        mappedHashes: map.size,
      }];

      // ADD THIS:
void ['[MatchesList] labelMap size:', labelMap.size];
void ['[MatchesList] crushHashMap size:', crushHashMap.size];
void ['[MatchesList] participants count:', participants.length];
void ['[MatchesList] labelMap keys:', Array.from(labelMap.keys())];
void ['[MatchesList] crushHashMap keys:', Array.from(crushHashMap.keys())];
    })();
  }, [userVault]);

  /* ========================================================================
   * DATA LOADING
   * ======================================================================== */
  async function reload() {
    try {
      setLoading(true);
      setError(null);
      
      const { userId } = await getCurrentUser();
      
      const [p, c] = await Promise.all([
        fetchParticipants(userId, 50, includeAll),
        fetchContactsForOwner(userId),
      ]);
      
void ['[MatchesList] ContactPoints fetched:', c.length]; // ADD THIS
void ['[MatchesList] ContactPoints data:', c]; // ADD THIS

      setParticipants(p);
      setContacts(c);
      
      const idx = await buildContactLabelIndex(c);
      setLabelMap(idx);
      
    } catch (e) {
      void ['[MatchesList] reload error:', e];
      setError(`Failed to load matches: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setLoading(false);
    }
  }

useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeAll]);

  // ✅ NEW: Listen for push notification match refresh events
  useEffect(() => {
    const handleRefresh = () => {
      void ['[MatchesList] 🔄 Refreshing matches from push notification'];
      reload();
    };
    
    window.addEventListener('crushhh-refresh-matches', handleRefresh);
    
    return () => {
      window.removeEventListener('crushhh-refresh-matches', handleRefresh);
    };
  }, []);

  // Rebuild labels if contacts change
  const contactsKey = useMemo(
    () => JSON.stringify(contacts.map(c => [c.kind, c.valueNormalized])),
    [contacts]
  );
  
  useEffect(() => {
    (async () => {
      if (!contacts.length) return;
      const idx = await buildContactLabelIndex(contacts);
      setLabelMap(idx);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactsKey]);

  // Build crush hash map when crushList changes
useEffect(() => {
  (async () => {
    const map = new Map<string, string>();
    
    for (const crush of crushList) {
      try {
        let hash: string;
        
        if (crush.contactHash) {
          hash = crush.contactHash;
        } else {
          // Compute hash for old format
          const norm = normalizeForHash(crush.contact);
          if (!norm) continue;
          hash = await sha256Hex(norm);
        }
        
        map.set(hash, crush.contact);
      } catch (err) {
        void ['[MatchesList] Error processing crush:', err];
      }
    }
    
    setCrushHashMap(map);
    void ['[MatchesList] crushHashMap built:', map.size, 'entries'];
    void ['[MatchesList] crushHashMap contents:', Array.from(map.entries())];
    void ['[MatchesList] Full vaultHashMap:', Array.from(map.entries())];
    void ['[MatchesList] crushHashMap full contents:', Array.from(map.entries())];
  })();
}, [crushList]);

  /* ========================================================================
   * ACTIONS
   * ======================================================================== */
  async function updateStatus(mp: MatchParticipant, next: Status) {
    const nowSec = Math.floor(Date.now() / 1000);
    const input: any = {
      userId: mp.userId,
      pairId: mp.pairId,
      status: next,
      hiddenAt: next === 'HIDDEN' ? nowSec : undefined,
    };

    try {
      const res = await client.graphql({
        query: M_UPDATE_PARTICIPANT,
        variables: { input },
        authMode: 'userPool',
      }) as GraphQLResult<any>;

      if (res.errors?.length) {
        void ['[MatchesList] updateMatchParticipant errors:', res.errors];
        throw new Error(res.errors[0].message);
      }

      // Optimistic: remove from local list when hidden
      if (next === 'HIDDEN') {
        setParticipants(prev => 
          prev.filter(m => !(m.userId === mp.userId && m.pairId === mp.pairId))
        );
      } else {
        reload();
      }
    } catch (error) {
      void ['[MatchesList] updateStatus error:', error];
      throw error;
    }
  }

  const handleHideClick = (matchId: string) => {
    setHidingMatchId(matchId);
  };

  const handleHideConfirm = async (mp: MatchParticipant) => {
    setHidingMatchId(null);
    try {
      await updateStatus(mp, 'HIDDEN');
      toast.success("Match hidden.");
    } catch {
      toast.error('Failed to hide. Please try again.');
    }
  };

  const handleHideCancel = () => {
    setHidingMatchId(null);
  };

  /* ========================================================================
   * RENDER
   * ======================================================================== */
  if (loading) return <div>Loading matches…</div>;
  if (error) return <div className="text-red-600">{error}</div>;
  
  // Hide entire component when no matches
  if (!participants.length) {
    return null;
  }

  return (
    <div className="mt-6 pt-4 ">
      <h3
        className="text-lg font-semibold text-gray-800 mb-2"
        style={{
          fontFamily: "'Fredoka', sans-serif",
          fontSize: 24,
          fontWeight: 700,
          color: "#1A1A1A",
        }}
      >Your Matches<span>        <button
            onClick={reload}
            className="px-3 py-1 ml-4 text-sm rounded-lg border hover:bg-gray-50"
            style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 700 }}
          >
            Refresh
          </button></span>
</h3>

      <div className="space-y-3">
        <div className="mb-2 flex items-center gap-3">
          {DEBUG && (
            <label className="text-sm flex items-center gap-2">
              <input 
                type="checkbox" 
                checked={includeAll} 
                onChange={e => setIncludeAll(e.target.checked)} 
              />
              Show all statuses (debug)
            </label>
          )}
        </div>

        {participants.map(mp => {
          // ✅ UPDATED: Priority lookup order
          const label =
            vaultHashMap.get(mp.otherHash) ??      // 1. User's vault (most reliable)
            labelMap.get(mp.otherHash) ??          // 2. ContactPoints table
            crushHashMap.get(mp.otherHash) ??      // 3. CrushList
            mp.matchLabel ??                       // 4. Server-provided label
            `Contact ${shortHash(mp.otherHash)}`; // 5. Fallback
          
                    // ADD THIS:
          void ['[MatchesList] Label lookup:', {
            otherHash: mp.otherHash.slice(0, 8),
            fullOtherHash: mp.otherHash,
            vaultFound: vaultHashMap.has(mp.otherHash),
            labelMapFound: labelMap.has(mp.otherHash),
            crushFound: crushHashMap.has(mp.otherHash),
            final: label,
          }];
          const meta = heartsFor(mp.sharedLevel ?? null);
          const isUnverified = mp.handleVerified === false;
          
          return (
            <div
              key={mp.pairId}
              style={{
                background: '#fff9f7',
                borderLeft: '4px solid #c0645a',
                borderRadius: 12,
                padding: 12,
                marginBottom: 8,
              }}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1">
                  {/* ✅ PENDING VERIFICATION BADGE */}
                  {isUnverified && (
                    <div className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-xs font-medium mb-1">
                      <span>⏳</span>
                      <span>Pending Verification</span>
                    </div>
                  )}

                  {/* ✅ MATCH LABEL */}
                  <div
                    className="text-lg font-semibold"
                    style={{
                      fontFamily: "'Nunito', sans-serif",
                      fontWeight: 700,
                      color: "#1A1A1A",
                    }}
                  >
                    {isUnverified ? (
                      <span className="text-gray-400">Verify to see match</span>
                    ) : (
                      extractHandle(label) || label
                    )}
                  </div>

                  {/* MATCH DATE */}
                  {mp.matchedAt != null && (
                    <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                      Matched {new Date(mp.matchedAt > 2_000_000_000 ? mp.matchedAt : mp.matchedAt * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </div>
                  )}

                  {/* INTEREST LEVEL */}
                  {meta && !isUnverified && (
                    <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-rose-50 text-rose-700 px-2 py-0.5 text-xs">
                      <span aria-hidden="true">{meta.hearts}</span>
                      <span>{meta.text}</span>
                    </div>
                  )}
                  
                  {/* DEBUG INFO */}
                  {DEBUG && (
                    <div className="mt-1 text-[11px] opacity-60">
                      pairId: {mp.pairId} • status: {mp.status} • 
                      otherHash: {shortHash(mp.otherHash)} • 
                      verified: {String(mp.handleVerified)} • 
                      owner: {mp.owner}
                    </div>
                  )}
                </div>
                
                {/* ACTIONS */}
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => handleHideClick(mp.pairId)}
                    className="px-3 py-1 text-sm rounded-lg border hover:bg-gray-50"
                    style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 700 }}
                  >
                    Hide
                  </button>
                </div>
              </div>

              {/* HIDE CONFIRMATION — full-width sibling below the label/actions row */}
              {hidingMatchId === mp.pairId && (
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                  <p style={{ fontSize: 12, color: '#888', margin: 0, textAlign: 'center', width: '100%' }}>
                    You will not see this match again and future matching with this user will be prevented.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'row', gap: 8, justifyContent: 'center', width: '100%' }}>
                    <button
                      onClick={handleHideCancel}
                      style={{
                        padding: '6px 16px',
                        borderRadius: 20,
                        border: '1px solid #ccc',
                        background: '#fff',
                        cursor: 'pointer',
                        fontSize: 13,
                        color: '#555',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleHideConfirm(mp)}
                      style={{
                        padding: '6px 16px',
                        borderRadius: 20,
                        border: 'none',
                        background: '#c0645a',
                        cursor: 'pointer',
                        fontSize: 13,
                        color: '#fff',
                        fontWeight: 700,
                      }}
                    >
                      Confirm Hide
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}